// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Primi passi con la Tracciabilità"
// Centro assistenza → guida per amministratori, solo piano TRACE.
//
// È la guida di ORIENTAMENTO del blocco tracciabilità: spiega la mappa, il
// vocabolario e l'ordine in cui si fanno le cose. Non insegna le singole
// schermate: quelle hanno una guida ciascuna (vedi docs/PIANO_GUIDE.md).
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Le etichette fra virgolette
// sono copiate dal codice.
//
// Riferimenti nel codice, per chi dovrà aggiornarla:
//   Menu del modulo .......... App.js, componente MenuTracciabilita
//                              (riorganizzato il 17/08/2026: se cambiano i
//                              gruppi, il capitolo 2 va allineato)
//   Stati lavorazione ........ backend/routes/tracciabilita.js — APERTA,
//                              CHIUSA, ANNULLATA. Nessuna riapertura: la
//                              chiusura risponde 409 "operazione non ripetibile"
//   Annullamento ............. /lavorazioni/:id/annulla, solo se APERTA e con
//                              motivazione obbligatoria
//   Fasi obbligatorie ........ guard "ITEM E" nella chiusura lavorazione
//   Formato del lotto ........ L.GGMMAA, derivato dalla data
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Callout, Passo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 't0', label: 'Prima di iniziare' },
  { id: 't1', label: '1. La mappa' },
  { id: 't2', label: '2. Il menu' },
  { id: 't3', label: '3. Il vocabolario' },
  { id: 't4', label: '4. In che ordine' },
  { id: 't5', label: '5. Il giro di ogni giorno' },
  { id: 't6', label: '6. Dove il sistema ti ferma' },
  { id: 't7', label: '7. Cosa non fa' },
  { id: 't8', label: 'Le trappole' },
  { id: 't9', label: 'Domande frequenti' },
];

// ─────── La catena della filiera ─────────────────────────────────────────────

const TAPPE = [
  ['factory',  'Fornitore',              'chi ti vende',        'config'],
  ['receipt',  'Bolla di ingresso',      'la merce arriva',     'entrata'],
  ['stamp',    'Accettazione',           'la controlli',        'entrata'],
  ['package',  'Magazzino MP',           'il lotto è a scorta', 'entrata'],
  ['chefHat',  'Lavorazione',            'ci produci',          'produzione'],
  ['pkg',      'Magazzino PF',           'il prodotto è pronto','uscita'],
  ['truck',    'Bolla di uscita',        'lo spedisci',         'uscita'],
  ['users',    'Cliente',                'chi lo compra',       'config'],
];

const COL = {
  config:     { bg: LT.arancioSoft, fg: '#92400E', bd: '#FDE9B5' },
  entrata:    { bg: LT.bluSoft,     fg: LT.blu,    bd: '#D8E6FE' },
  produzione: { bg: LT.verdeSoft,   fg: '#166534', bd: '#C7EFDA' },
  uscita:     { bg: LT.violaSoft,   fg: LT.viola,  bd: '#DDD3FA' },
};

const Filiera = () => (
  <div style={{
    display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 6,
    margin: '22px 0', padding: 16,
    background: LT.n50, border: `1px solid ${LT.n150}`, borderRadius: LT.r.lg,
  }}>
    {TAPPE.map(([ic, nome, cosa, kind], i) => {
      const c = COL[kind];
      return (
        <React.Fragment key={nome}>
          <div style={{
            flex: '1 1 116px', minWidth: 116,
            background: c.bg, border: `1px solid ${c.bd}`, borderRadius: LT.r.md,
            padding: '10px 9px', textAlign: 'center',
          }}>
            <Ico d={I[ic]} size={18} stroke={c.fg} />
            <div style={{ fontSize: 12.5, fontWeight: 800, color: c.fg, marginTop: 4, lineHeight: 1.2 }}>
              {nome}
            </div>
            <div style={{ fontSize: 11, color: LT.n500, marginTop: 2, lineHeight: 1.3 }}>
              {cosa}
            </div>
          </div>
          {i < TAPPE.length - 1 && (
            <div style={{
              display: 'flex', alignItems: 'center', color: LT.n400, flex: '0 0 auto',
            }}>
              <Ico d={I.arrowRight} size={14} stroke={LT.n400} />
            </div>
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const Voce = ({ parola, spiega }) => (
  <div style={{
    display: 'flex', gap: 12, alignItems: 'flex-start',
    padding: '10px 0', borderBottom: `1px solid ${LT.n150}`,
  }}>
    <div style={{ fontSize: 13.5, fontWeight: 700, color: LT.navy, width: 150, flexShrink: 0 }}>
      {parola}
    </div>
    <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.55 }}>{spiega}</div>
  </div>
);

export default function PrimiPassiTracciabilita() {
  return (
    <GuidaLayout
      titolo="Primi passi con la Tracciabilità"
      sottotitolo="La mappa del modulo, le parole da conoscere e l'ordine in cui si fanno le cose. Da leggere una volta prima di toccare qualsiasi schermata: dopo, tutto il resto ha senso."
      durata="~ 15 minuti"
      destinatario="Per amministratori · piano TRACE"
      sezioni={SEZIONI}
    >

      {/* t0 */}
      <H id="t0">Prima di iniziare</H>

      <P>
        La Tracciabilità è il secondo blocco di LabThree, disponibile con il piano TRACE. Serve a
        rispondere a due domande che prima o poi arrivano: <Strong>da dove viene questo
        prodotto</Strong> e <Strong>dove è finito quel lotto</Strong>. La prima la fa un
        ispettore, la seconda ve la fate voi il giorno in cui bisogna richiamare qualcosa.
      </P>
      <P>
        È la parte più articolata del programma, perché segue il prodotto dal fornitore al
        cliente passando per la produzione. Ma è articolata come è articolato il vostro lavoro:
        non c'è un passaggio che non corrisponda a qualcosa che già fate.
      </P>

      <Callout kind="info" titolo="Questa guida non insegna le schermate">
        Serve a capire come sta insieme il modulo. Ogni schermata ha poi la sua guida — le
        anagrafiche, la ricetta, l'accettazione, la lavorazione, le giacenze — e le trovi in
        questo stesso Centro assistenza. Leggi prima questa: sono quindici minuti che ti
        risparmiano un pomeriggio.
      </Callout>

      <P>
        Una premessa che vale la pena mettere subito: la Tracciabilità{' '}
        <Strong>non funziona a metà</Strong>. Non è come le sezioni HACCP, dove ogni registro
        vive per conto suo. Qui ogni passaggio si appoggia al precedente, e finché non c'è il
        precedente il successivo non si può nemmeno cominciare. Non è una limitazione: è quello
        che rende la catena una catena.
      </P>

      {/* t1 */}
      <H id="t1" cat="produzione">1. La mappa</H>

      <P>
        Tutto il modulo è questo. Otto tappe, da sinistra a destra, nell'ordine in cui il
        prodotto attraversa la vostra azienda.
      </P>

      <Filiera />

      <P>
        Le tappe in arancione sono anagrafiche: si compilano una volta e restano. Quelle in
        azzurro sono le entrate, quella verde è la produzione, quelle viola le uscite. È lo
        stesso ordine, e sono gli stessi colori, dei gruppi del menu.
      </P>
      <P>
        La regola che tiene insieme la catena si legge da sé guardando le frecce:{' '}
        <Strong>senza fornitori non registri un ingresso, senza ingressi non hai lotti da
        lavorare, senza lavorazioni non hai prodotto da spedire.</Strong> Se una tendina ti
        sembra vuota, quasi sempre manca la tappa a monte.
      </P>

      {/* t2 */}
      <H id="t2">2. Il menu</H>

      <P>
        Aprendo <Code>Tracciabilità</Code> trovi le voci raggruppate nello stesso ordine della
        mappa: prima quello che si usa ogni giorno, in fondo quello che si imposta una volta.
      </P>

      <div style={{ margin: '18px 0' }}>
        {[
          ['Dashboard', 'In cima, da sola. Lo stato del modulo in quattro numeri: lavorazioni aperte, chiuse nel mese, giacenze, materie prime in scadenza.', LT.n100, LT.n600],
          ['Entrate', 'Bolle di ingresso, Ricevimento / Accettazione, Magazzino materie prime.', LT.bluSoft, LT.blu],
          ['Produzione', 'Lavorazioni e Giacenze per ubicazione.', LT.verdeSoft, '#166534'],
          ['Uscite', 'Magazzino prodotti finiti e Bolle di uscita.', LT.violaSoft, LT.viola],
          ['Verifiche e controlli', 'Test di tracciabilità e Bilancio di massa. Si usano quando arriva un controllo o quando vuoi misurare una resa.', LT.cianoSoft, '#155E75'],
          ['Configurazione', "Le otto cose che si impostano all'avvio: fornitori, clienti, ingredienti, prodotti, fasi, ricette, ubicazioni, modelli di accettazione.", LT.arancioSoft, '#92400E'],
        ].map(([g, cosa, bg, fg]) => (
          <div key={g} style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
            padding: '10px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <span style={{
              flexShrink: 0, padding: '4px 10px', borderRadius: 999,
              fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.4,
              background: bg, color: fg, width: 150, textAlign: 'center',
            }}>{g}</span>
            <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.55 }}>{cosa}</div>
          </div>
        ))}
      </div>

      <Callout kind="tip" titolo="Il gruppo in fondo è quello da cui si comincia">
        Sembra un controsenso, ed è voluto. <Strong>Configurazione</Strong> sta in basso perché
        la si usa nelle prime settimane e poi quasi mai, mentre chi lavora in produzione ha le
        sue voci in cima ogni mattina. Ma se stai partendo da zero, il tuo lavoro è tutto lì
        dentro.
      </Callout>

      {/* t3 */}
      <H id="t3" cat="formazione">3. Il vocabolario</H>

      <P>
        Sette parole. Se sono chiare queste, il resto del modulo si spiega da sé.
      </P>

      <div style={{ margin: '18px 0' }}>
        <Voce parola="Ingrediente" spiega="Quello che compri e che entra nel prodotto: il latte, la carne, il sale, le spezie. Include anche i materiali a contatto — buste, budelli, vasetti — che nel programma si chiamano MOCA." />
        <Voce parola="Prodotto" spiega="Quello che vendi: la caciotta, il salame. Ha un nome, una categoria e i suoi allergeni." />
        <Voce parola="Semilavorato" spiega="Quello che produci per usarlo in un'altra lavorazione, come l'impasto per il salame. È un prodotto a tutti gli effetti, ma nasce da una lavorazione e ne alimenta un'altra: è quello che permette di ricostruire la catena su due livelli." />
        <Voce parola="Lotto" spiega={<>
          Il codice che identifica una produzione. All'apertura di una lavorazione il campo{' '}
          <Strong>Lotto prodotto</Strong> arriva già compilato dal sistema partendo dalla data,
          nel formato <Code>L.GGMMAA</Code>: una lavorazione avviata il 17 agosto 2026 propone{' '}
          <Code>L.170826</Code>. Ma è un campo di testo libero e{' '}
          <Strong>lo puoi scrivere come vuoi</Strong>: se in azienda avete già una vostra
          codifica dei lotti, usate quella. Molti aggiungono un suffisso per distinguere il
          prodotto, come <Code>L.170826.CAC</Code> per la caciotta e <Code>L.170826.IMP</Code>{' '}
          per l'impasto.
        </>} />
        <Voce parola="Fase" spiega="Un passaggio della produzione: impasto, insacco, stagionatura. Ha una durata, può avere dei parametri da rilevare, e si svolge in un posto preciso." />
        <Voce parola="Ricetta" spiega="La descrizione di come si fa un prodotto: quali ingredienti, in che quantità, in quali fasi e in che ordine. È lo stampo da cui nasce ogni lavorazione." />
        <Voce parola="Ubicazione" spiega="Un posto fisico del vostro stabilimento: la sala di lavorazione, la cantina di stagionatura, il magazzino dei confezionati. Serve a sapere dove si trova il prodotto adesso." />
      </div>

      <Callout kind="warn" titolo="Due lavorazioni dello stesso giorno hanno lo stesso lotto">
        Se lasciate il codice automatico, due produzioni avviate nella stessa giornata
        condividono il lotto, perché nasce dalla data. Non è un errore ed è normale in molte
        aziende, ma vuol dire che il lotto da solo non basta a distinguerle — nelle schermate
        dove serve, il programma mostra anche il numero della lavorazione.
        {' '}Se invece vi serve che ogni produzione abbia un codice suo, la soluzione è a portata
        di mano: <Strong>scrivetelo voi</Strong> nel campo Lotto prodotto, aggiungendo un
        suffisso o seguendo la vostra codifica.
      </Callout>

      <Callout kind="tip" titolo="Il sistema non ti sovrascrive la scelta">
        Un dettaglio comodo: se cambi la data di inizio della lavorazione, il lotto proposto si
        aggiorna con la data nuova <em>soltanto</em> se non lo avevi modificato. Se ci avevi
        scritto il tuo codice, resta il tuo.
      </Callout>

      {/* t4 */}
      <H id="t4">4. In che ordine</H>

      <P>
        Se state partendo da zero, questo è l'ordine. Non è un suggerimento: ogni passo ha
        bisogno del precedente, e saltandone uno il successivo trova le tendine vuote.
      </P>

      <Passo n={1} titolo="Le ubicazioni">
        <P style={{ margin: '6px 0' }}>
          Elencate i posti dove il prodotto si ferma. Non serve mappare lo stabilimento stanza
          per stanza: bastano i luoghi che vi interessa distinguere quando cercate qualcosa.
        </P>
      </Passo>
      <Passo n={2} titolo="Gli ingredienti e i prodotti">
        <P style={{ margin: '6px 0' }}>
          Prima cosa comprate, poi cosa vendete. Qui si mettono anche gli allergeni, e vale la
          pena farlo bene subito: da lì nascono le schede tecniche.
        </P>
      </Passo>
      <Passo n={3} titolo="Le fasi produttive">
        <P style={{ margin: '6px 0' }}>
          I passaggi che usate, con i parametri da rilevare se ce ne sono. Sono in comune fra le
          ricette: la stagionatura si descrive una volta e la usano tutti i prodotti che la
          prevedono.
        </P>
      </Passo>
      <Passo n={4} titolo="Le ricette">
        <P style={{ margin: '6px 0' }}>
          Qui si unisce tutto: per ogni prodotto, quali ingredienti in che quantità e attraverso
          quali fasi. È il passo più lungo e quello che vi ripaga di più, perché da qui in avanti
          ogni lavorazione parte già compilata.
        </P>
      </Passo>
      <Passo n={5} titolo="Fornitori e clienti">
        <P style={{ margin: '6px 0' }}>
          Chi vi vende e chi vi compra. Dei clienti servono i contatti veri, perché sono quelli
          che userete se un giorno bisogna avvisare qualcuno.
        </P>
      </Passo>
      <Passo n={6} titolo="I modelli di accettazione">
        <P style={{ margin: '6px 0' }}>
          La lista di cosa controllate quando arriva la merce: temperatura, integrità
          dell'imballo, documenti. Si prepara una volta e poi il ricevimento la propone da sé.
        </P>
      </Passo>

      <Callout kind="success" titolo="Da qui in poi si lavora">
        Fatti questi sei passi, il modulo è pronto e non ci si torna quasi più. Tutto il resto —
        bolle, accettazioni, lavorazioni, spedizioni — è il lavoro di ogni giorno.
      </Callout>

      {/* t5 */}
      <H id="t5" cat="temperature">5. Il giro di ogni giorno</H>

      <P>
        Con la configurazione a posto, una giornata tipo attraversa il modulo così.
      </P>

      <P>
        <Strong>Arriva la merce.</Strong> Si registra la <em>bolla di ingresso</em> con i lotti
        del fornitore, e si compila l'<em>accettazione</em>: se qualcosa non va, da lì nasce una
        non conformità senza doverla scrivere a mano. I lotti finiscono nel{' '}
        <em>magazzino materie prime</em>, dove restano a scorta con la loro scadenza.
      </P>
      <P>
        <Strong>Si produce.</Strong> Si apre una <em>lavorazione</em> scegliendo il prodotto: la
        ricetta suggerisce gli ingredienti e voi indicate quali lotti state usando davvero — ed è
        questo il gesto che crea la tracciabilità, perché lega il vostro lotto a quelli del
        fornitore. Poi si avanza fase per fase, dichiarando quanto prodotto passa avanti e in
        quale ubicazione si trova.
      </P>
      <P>
        <Strong>Si chiude.</Strong> Alla fine della lavorazione si dichiarano i dati finali e{' '}
        <em>dove resta il prodotto</em>. Nasce il lotto di prodotto finito, che entra nel{' '}
        <em>magazzino prodotti finiti</em>.
      </P>
      <P>
        <Strong>Si spedisce.</Strong> La <em>bolla di uscita</em> collega il lotto al cliente. È
        l'ultimo anello, e in caso di richiamo è quello che dice a chi telefonare.
      </P>

      {/* t6 */}
      <H id="t6">6. Dove il sistema ti ferma</H>

      <P>
        Ci sono punti in cui il programma dice no. Sono pochi e sono tutti voluti: conoscerli
        evita di pensare a un guasto.
      </P>

      <div style={{ margin: '18px 0' }}>
        {[
          ['Una lavorazione chiusa non si riapre',
            'La chiusura è definitiva e il messaggio lo dice: "operazione non ripetibile". Un registro di produzione che si può riscrivere a posteriori non dimostrerebbe niente. Se hai chiuso troppo presto, la strada è una rettifica sul magazzino, non la riapertura.'],
          ['Le fasi non si saltano',
            'Ogni fase va aperta e chiusa, e la chiusura della lavorazione chiude l\'ultima. Se provi a chiudere lasciando indietro delle fasi, il programma ti elenca quali.'],
          ['Non si sposta più prodotto di quanto ce n\'è',
            'Spostamenti, scarti e avanzamenti si fermano alla giacenza effettiva dell\'ubicazione, sia in pezzi sia in chili.'],
          ['I dati finali devono coincidere con le ubicazioni',
            'Alla chiusura, quello che dichiari di aver prodotto deve corrispondere a quello che resta fisicamente nelle celle. Se una parte è andata persa, si abbassano le righe e la differenza viene registrata come calo.'],
          ['Una data di movimento non può precedere la fase',
            'Un passaggio avvenuto prima che la fase esistesse non ha senso, e viene rifiutato.'],
        ].map(([t, d]) => (
          <div key={t} style={{
            display: 'flex', gap: 11, alignItems: 'flex-start',
            padding: '11px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <Ico d={I.lock} size={16} stroke={LT.n500} style={{ marginTop: 3, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>{t}</div>
              <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.55, marginTop: 2 }}>{d}</div>
            </div>
          </div>
        ))}
      </div>

      <Callout kind="info" titolo="Le quantità consumate, invece, non bloccano mai">
        Quanto avete usato di un lotto è una <Strong>dichiarazione</Strong>, non un vincolo. Si
        può usare un lotto quasi esaurito, correggere una quantità dopo, tollerare le differenze
        di pesata. È una scelta precisa: nella realtà di un laboratorio i numeri non tornano al
        grammo, e un programma che pretendesse il contrario finirebbe per essere aggirato.
      </Callout>

      <P>
        Una lavorazione aperta per sbaglio si può <Strong>annullare</Strong>, indicando il
        motivo. Funziona solo finché è aperta: una volta chiusa, non si torna indietro.
      </P>

      {/* t7 */}
      <H id="t7">7. Cosa non fa</H>

      <P>
        Detto prima che lo scopriate da soli, perché sapere dove si fermano gli strumenti fa
        parte del saperli usare.
      </P>
      <P>
        Non è un gestionale e non fa fatturazione: le bolle servono alla tracciabilità, non alla
        contabilità. Non calcola i costi di produzione. Non parla con la bilancia né con le
        sonde: i valori si inseriscono a mano. Non decide per voi se un lotto è conforme — mette
        in fila i dati perché possiate deciderlo voi, e conserva la prova di come avete deciso.
      </P>

      {/* t8 */}
      <H id="t8">Le trappole</H>

      <div style={{ margin: '16px 0' }}>
        {[
          ['"La tendina è vuota"',
            'Manca la tappa a monte. Nessun lotto da lavorare vuol dire nessuna bolla di ingresso registrata; nessun ingrediente da scegliere vuol dire ricetta incompleta.'],
          ['Ricette e lavorazioni in corso',
            'Modificare una ricetta mentre una lavorazione la sta usando cambia il terreno sotto i piedi. Se devi aggiornarla, fallo quando non ci sono lavorazioni aperte su quel prodotto.'],
          ['Il lotto automatico non identifica la lavorazione',
            'Due produzioni dello stesso giorno condividono il codice derivato dalla data. Quando devi essere sicuro di quale stai guardando usa il numero della lavorazione — oppure, meglio, personalizza il lotto quando la apri.'],
          ['Non serve mappare tutto',
            "Ubicazioni e fasi vanno descritte al livello di dettaglio che vi serve davvero. Dieci ubicazioni usate bene valgono più di quaranta compilate per scrupolo e mai aggiornate."],
        ].map(([t, d]) => (
          <div key={t} style={{
            display: 'flex', gap: 11, alignItems: 'flex-start',
            padding: '11px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <Ico d={I.alert} size={16} stroke={LT.arancio} style={{ marginTop: 3, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>{t}</div>
              <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.55, marginTop: 2 }}>{d}</div>
            </div>
          </div>
        ))}
      </div>

      {/* t9 */}
      <H id="t9">Domande frequenti</H>

      <FAQ q="Quanto tempo serve per partire?">
        La configurazione dei sei passi richiede da mezza giornata a un paio di giorni, e dipende
        quasi solo da quante ricette avete. Il consiglio è di non configurare tutto: prendete un
        prodotto, portatelo fino in fondo — ricetta, ingresso, lavorazione, spedizione — e solo
        dopo aggiungete gli altri. Si impara molto più in fretta e gli errori costano meno.
      </FAQ>

      <FAQ q="Devo usare la Tracciabilità per tutti i prodotti?">
        No, e all'inizio è meglio non farlo. Il modulo funziona anche su un prodotto solo. Le
        aziende che partono da tutto il catalogo si arenano, quelle che partono da un prodotto
        arrivano in fondo.
      </FAQ>

      <FAQ q="Posso registrare le lavorazioni dei giorni scorsi?">
        Sì. Le date dei passaggi si possono indicare a mano, quindi si può mettere in ordine
        anche una produzione già fatta. L'unico limite è di buon senso, e il programma lo
        applica: un movimento non può essere datato prima dell'inizio della sua fase.
      </FAQ>

      <FAQ q="Che differenza c'è tra il magazzino materie prime e le giacenze per ubicazione?">
        Il magazzino materie prime tiene i lotti che avete comprato e non ancora consumato. Le
        giacenze per ubicazione dicono dove si trova adesso il prodotto vostro, quello in
        lavorazione e quello finito. Il primo guarda a monte, il secondo dentro lo stabilimento.
      </FAQ>

      <FAQ q="Se sbaglio qualcosa nella configurazione, ho rovinato tutto?">
        No. Ubicazioni, fasi, ricette e anagrafiche si correggono in qualsiasi momento.
        L'unica cosa che conviene fare con attenzione sono le ricette usate da lavorazioni
        aperte. Le lavorazioni chiuse, quelle sì, non si toccano più: ma è esattamente il motivo
        per cui valgono come prova.
      </FAQ>

      <div style={{
        marginTop: 40, padding: 18, background: LT.bluSoft,
        borderRadius: LT.r.lg, border: `1px solid ${LT.n150}`,
      }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
          E adesso?
        </div>
        <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.65 }}>
          Parti dal gruppo <Strong>Configurazione</Strong> e segui i sei passi del capitolo 4, in
          quell'ordine. Prendi <em>un</em> prodotto e portalo fino alla spedizione: quando ci sei
          riuscito una volta, hai capito tutto il modulo. Le guide dedicate alle singole
          schermate ti aspettano qui, in questo Centro assistenza.
        </div>
      </div>

    </GuidaLayout>
  );
}
